/**
 * AgentConfigPopup - Configure Agent Mode Settings
 * 
 * This popup provides:
 * - Select agent preset (Assistant, Researcher, Coder, etc.)
 * - Configure max steps
 * - Set custom instructions
 * - Adjust temperature
 */

import React, { memo, useCallback, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
    Button,
    PopupWrapper,
    TextLabel,
} from '@acme/ui';
import type { AgentPreset } from '@acme/util';
import {
    globalAgentConfigAtom,
    modelPopupAtom,
    AGENT_PRESETS,
    DEFAULT_AGENT_CONFIG,
} from '../store/instruction';
import { RangeWithLabel } from '../chat/ComposeMessage';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AgentConfigPopup = memo(() => {
    const [popup, setPopup] = useAtom(modelPopupAtom);
    const [agentConfig, setAgentConfig] = useAtom(globalAgentConfigAtom);
    const [localConfig, setLocalConfig] = useState(agentConfig);

    const isOpen = popup.open && popup.type === 'agent-config';

    const handleClose = useCallback(() => {
        setPopup({ open: false, type: '' });
    }, [setPopup]);

    const handleSave = useCallback(() => {
        setAgentConfig(localConfig);
        handleClose();
    }, [localConfig, setAgentConfig, handleClose]);

    const handleReset = useCallback(() => {
        setLocalConfig(DEFAULT_AGENT_CONFIG);
    }, []);

    const selectPreset = useCallback((preset: AgentPreset) => {
        setLocalConfig(prev => ({
            ...prev,
            preset,
            customInstructions: preset === 'custom' ? prev.customInstructions : undefined,
        }));
    }, []);

    if (!isOpen) return null;

    return (
        <PopupWrapper
            show={isOpen}
            onClose={handleClose}
            className="!bg-black/60"
            containerClassName="z-[100002]"
        >
            <div className="w-full max-w-lg bg-white dark:bg-neutral-900 rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-neutral-700">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">🤖</span>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Agent Configuration
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Configure how the AI agent behaves
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
                    {/* Preset Selection */}
                    <div>
                        <TextLabel size="md" className="mb-3 text-gray-700 dark:text-gray-300">
                            Agent Preset
                        </TextLabel>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.entries(AGENT_PRESETS).map(([key, preset]) => {
                                const isSelected = localConfig.preset === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => selectPreset(key as AgentPreset)}
                                        className={`
                                            flex items-start gap-3 p-3 rounded-lg text-left transition-all
                                            ${isSelected
                                                ? 'bg-purple-100 dark:bg-purple-900/40 ring-2 ring-purple-500'
                                                : 'bg-gray-50 dark:bg-neutral-800 hover:bg-gray-100 dark:hover:bg-neutral-700'
                                            }
                                        `}
                                    >
                                        <span className="text-xl mt-0.5">{preset.icon}</span>
                                        <div>
                                            <p className={`text-sm font-medium ${isSelected
                                                    ? 'text-purple-700 dark:text-purple-300'
                                                    : 'text-gray-900 dark:text-white'
                                                }`}>
                                                {preset.name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {preset.description}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Custom Instructions (only for custom preset) */}
                    {localConfig.preset === 'custom' && (
                        <div>
                            <TextLabel size="md" className="mb-2 text-gray-700 dark:text-gray-300">
                                Custom Instructions
                            </TextLabel>
                            <textarea
                                value={localConfig.customInstructions || ''}
                                onChange={e => setLocalConfig(prev => ({
                                    ...prev,
                                    customInstructions: e.target.value
                                }))}
                                placeholder="You are a helpful AI agent that..."
                                rows={4}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            />
                        </div>
                    )}

                    {/* Max Steps */}
                    <div>
                        <RangeWithLabel
                            label={`Max Steps: ${localConfig.maxSteps}`}
                            values={[localConfig.maxSteps]}
                            min={1}
                            max={30}
                            step={1}
                            minLabel="1 step"
                            maxLabel="30 steps"
                            size="md"
                            labelClassName="text-gray-700 dark:text-gray-300"
                            onChange={values => setLocalConfig(prev => ({
                                ...prev,
                                maxSteps: values[0] ?? 10
                            }))}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Maximum number of tool-calling steps before the agent stops
                        </p>
                    </div>

                    {/* Temperature */}
                    <div>
                        <RangeWithLabel
                            label={`Temperature: ${(localConfig.temperature ?? 0.7).toFixed(1)}`}
                            values={[localConfig.temperature ?? 0.7]}
                            min={0}
                            max={1}
                            step={0.1}
                            minLabel="Focused"
                            maxLabel="Creative"
                            size="md"
                            labelClassName="text-gray-700 dark:text-gray-300"
                            onChange={values => setLocalConfig(prev => ({
                                ...prev,
                                temperature: values[0] ?? 0.7
                            }))}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Higher values make output more creative but less predictable
                        </p>
                    </div>

                    {/* Agent Info */}
                    <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                        <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                            💡 How Agent Mode Works
                        </h4>
                        <ul className="text-xs text-purple-600 dark:text-purple-400 space-y-1">
                            <li>• Agent can use tools autonomously to complete tasks</li>
                            <li>• It will plan and execute multiple steps if needed</li>
                            <li>• Works best with clear, goal-oriented prompts</li>
                            <li>• Progress is shown as the agent works</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-neutral-700 flex justify-between">
                    <Button
                        onClick={handleReset}
                        className="!bg-gray-200 dark:!bg-neutral-700 !text-gray-700 dark:!text-gray-300"
                    >
                        Reset to Default
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            onClick={handleClose}
                            className="!bg-gray-200 dark:!bg-neutral-700 !text-gray-700 dark:!text-gray-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            className="!bg-purple-600 hover:!bg-purple-700 !text-white px-6"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </PopupWrapper>
    );
});

AgentConfigPopup.displayName = 'AgentConfigPopup';

export default AgentConfigPopup;
